// SPDX-License-Identifier: BUSL-1.1

/**
 * UnifiedTimelineCard — dashboard card showing GitHub + agent + governance activity.
 *
 * Fetches from GET /api/orgs/:slug/timeline and renders a filtered, time-grouped
 * activity stream. Replaces the basic ActivityFeed card on the dashboard.
 *
 * Filter types:
 *   all        — Everything (default)
 *   github     — GitHub events only (push, PR, review, release)
 *   agent      — Agent activity only (sessions, episodes, decisions)
 *   governance — Config/flag changes from audit log
 */

import * as React from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Bot, GitFork, Settings, ChevronDown, ExternalLink } from 'lucide-react';
import { apiFetch } from '@/lib/api-client';
import { useCurrentOrg } from '@/contexts/org-context';
import { relativeTime } from '@/lib/format';
import { formatAuditAction } from '@/lib/format-audit';
import type { AuditEntry } from '@/types/api';
import { cn } from '@/lib/utils';

export type TimelineFilterType = 'all' | 'github' | 'agent' | 'governance';

interface TimelineEntry {
  id: string;
  type: 'github' | 'agent_activity';
  timestamp: string;
  source: string;
  title: string;
  metadata: Record<string, unknown>;
}

interface TimelineResponse {
  timeline: TimelineEntry[];
  count: number;
}

const FILTER_LABELS: Record<TimelineFilterType, string> = {
  all: 'All',
  github: 'GitHub',
  agent: 'Agent',
  governance: 'Governance',
};

const FILTER_ICON: Record<TimelineFilterType, React.ElementType> = {
  all: ExternalLink,
  github: GitFork,
  agent: Bot,
  governance: Settings,
};

interface UnifiedTimelineCardProps {
  /** Maximum number of entries to show. Default 6. */
  maxItems?: number;
  /** Default filter. Default 'all'. */
  defaultFilter?: TimelineFilterType;
  /** URL for "View all" link. Default '/timeline'. */
  viewAllHref?: string;
}

export function UnifiedTimelineCard({
  maxItems = 6,
  defaultFilter = 'all',
  viewAllHref = '/timeline',
}: UnifiedTimelineCardProps): React.ReactElement {
  const { currentOrg } = useCurrentOrg();
  const slug = currentOrg?.slug ?? '';
  const [filter, setFilter] = React.useState<TimelineFilterType>(defaultFilter);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['unified-timeline', slug, filter],
    queryFn: () =>
      apiFetch<TimelineResponse>(
        `/orgs/${slug}/timeline?hours=24&limit=${maxItems}`,
      ),
    enabled: !!slug,
    staleTime: 30_000,
    retry: 1,
  });

  const entries = (data?.timeline ?? []).filter((e) => {
    if (filter === 'all') return true;
    if (filter === 'github') return e.type === 'github';
    if (filter === 'agent') return e.type === 'agent_activity';
    if (filter === 'governance') return false; // audit entries not in /timeline yet
    return true;
  });

  const FilterIcon = FILTER_ICON[filter];

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <div className="flex items-center gap-2">
          <CardTitle className="text-sm">Unified Timeline</CardTitle>
          {data?.count !== undefined && (
            <span className="rounded bg-neutral-800 px-1.5 py-0.5 text-[10px] font-mono text-neutral-500">
              {data.count}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Filter dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger>
              <Button variant="ghost" size="sm" className="h-7 gap-1 text-xs text-neutral-400">
                <FilterIcon className="size-3" />
                {FILTER_LABELS[filter]}
                <ChevronDown className="size-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {(Object.keys(FILTER_LABELS) as TimelineFilterType[]).map((f) => {
                const Icon = FILTER_ICON[f];
                return (
                  <DropdownMenuItem
                    key={f}
                    onClick={() => setFilter(f)}
                    className={cn('gap-2', filter === f && 'bg-neutral-800')}
                  >
                    <Icon className="size-3" />
                    {FILTER_LABELS[f]}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>

          <Link
            to={viewAllHref}
            className="text-xs text-neutral-500 transition hover:text-neutral-300"
          >
            View all →
          </Link>
        </div>
      </CardHeader>

      <CardContent className="p-0">
        {isLoading && (
          <div className="flex flex-col gap-3 px-6 py-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3">
                <Skeleton className="h-4 w-12" />
                <Skeleton className="h-4 w-4 rounded-full" />
                <Skeleton className="h-4 flex-1" />
              </div>
            ))}
          </div>
        )}

        {isError && (
          <p className="px-6 py-6 text-center text-sm text-red-400">
            Failed to load timeline.
          </p>
        )}

        {!isLoading && !isError && entries.length === 0 && (
          <p className="px-6 py-8 text-center text-sm text-neutral-500">
            No {filter === 'all' ? 'activity' : FILTER_LABELS[filter].toLowerCase()} in the last 24 hours.
          </p>
        )}

        {!isLoading && !isError && entries.length > 0 && (
          <ul className="divide-y divide-neutral-800">
            {entries.slice(0, maxItems).map((entry) => (
              <TimelineItem key={entry.id} entry={entry} />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function TimelineItem({ entry }: { entry: TimelineEntry }): React.ReactElement {
  const timeLabel = relativeTime(entry.timestamp);
  const fullTimestamp = new Date(entry.timestamp).toLocaleString();

  const sourceConfig = {
    github: {
      icon: GitFork,
      color: 'text-purple-400',
      badge: 'bg-purple-900/40 text-purple-300',
      label: 'GitHub',
    },
    agent_activity: {
      icon: Bot,
      color: 'text-green-400',
      badge: 'bg-green-900/40 text-green-300',
      label: 'Agent',
    },
  } as const;

  const config = sourceConfig[entry.type] ?? sourceConfig.github;
  const Icon = config.icon;

  // Build a synthetic AuditEntry for formatAuditAction (for governance entries in the future)
  const syntheticEntry: AuditEntry = {
    id: entry.id,
    actorId: entry.source,
    actorType: entry.type === 'agent_activity' ? 'agent' : 'human',
    action: entry.source,
    resourceType: entry.type,
    resourceId: entry.id,
    createdAt: entry.timestamp,
  };

  const formatted = formatAuditAction(syntheticEntry);

  return (
    <li className="flex items-start gap-3 px-6 py-3 transition hover:bg-neutral-900/50">
      <time
        className="w-14 shrink-0 pt-0.5 font-mono text-xs text-neutral-500"
        dateTime={entry.timestamp}
        title={fullTimestamp}
      >
        {timeLabel}
      </time>

      {/* Source badge */}
      <span
        className={cn(
          'mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full',
          config.badge,
        )}
      >
        <Icon className={cn('size-3', config.color)} />
      </span>

      <div className="flex-1 min-w-0">
        <p className="text-sm text-neutral-400">
          <span className={cn('font-medium', config.color)}>{config.label}</span>
          {' — '}
          <span className="text-neutral-300">{entry.title}</span>
        </p>
        {/* Show key metadata as subtitle */}
        {entry.metadata && Object.keys(entry.metadata).length > 0 && (
          <p className="mt-0.5 truncate text-xs text-neutral-600">
            {renderMetadata(entry.metadata)}
          </p>
        )}
      </div>
    </li>
  );
}

function renderMetadata(meta: Record<string, unknown>): string {
  const parts: string[] = [];
  if (meta.repo) parts.push(String(meta.repo));
  if (meta.branch) parts.push(String(meta.branch));
  if (meta.commits) parts.push(`${meta.commits} commit${Number(meta.commits) === 1 ? '' : 's'}`);
  if (meta.pr_number) parts.push(`PR #${meta.pr_number}`);
  if (meta.actor) parts.push(`by ${String(meta.actor)}`);
  return parts.join(' · ');
}
